import PropTypes from 'prop-types';
import React, { useRef, useEffect, useCallback, useState } from 'react';

const DraggableWrapper = ({
  wrapperRef,
  isDraggable,
  updatePlayerState,
  bottomMargin = 130, // Default bottom margin (e.g., for sticky footer)
  topMargin = 40, // Default top margin (e.g., for sticky header)
  animation,
}) => {
  const isDragging = useRef(false);
  const offset = useRef({ x: 0, y: 0 });
  const startPos = useRef({ x: 0, y: 0 });
  const scrollTimeoutRef = useRef(null);
  const [hasBeenDragged, setHasBeenDragged] = useState(false);

  const mouseDownPos = useRef({ x: 0, y: 0 });
  const isClick = useRef(true);
  // Reference for event listeners to avoid dependency cycles
  const listeners = useRef({});
  // Reference to store initial setup state
  const setupState = useRef({
    isDraggableAdded: false,
    initialRight: 10,
    initialBottom: bottomMargin + 10,
  });

  // Use transform instead of changing left/top properties for better performance
  const updatePosition = useCallback(
    (x, y, rest = {}) => {
      if (wrapperRef.current) {
        updatePlayerState({
          wrapperStyle: {
            top: '0',
            right: 'auto',
            bottom: 'auto',
            transform: `translate3d(${x}px, ${y}px, 0)`,
            ...rest,
          },
        });
        // wrapperRef.current.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      }
    },
    [wrapperRef],
  );

  // Initial position setup with transform instead of position properties
  useEffect(
    () => {
      if (
        wrapperRef.current &&
        isDraggable &&
        !setupState.current.isDraggableAdded
      ) {
        updatePlayerState({
          wrapperStyle: {
            right: `${setupState.current.initialRight}px`,
            bottom: `${setupState.current.initialBottom}px`,
          },
          isDraggable,
        });
        setupState.current.isDraggableAdded = true;
        // Apply animation class only if component hasn't been dragged
      }
      if (wrapperRef.current && animation) {
        if (isDraggable && !hasBeenDragged) {
          wrapperRef.current.classList.add(animation);
        } else {
          wrapperRef.current.classList.remove(animation);
        }
      }
    },
    [isDraggable, updatePlayerState, wrapperRef, hasBeenDragged, animation],
  );

  // Scroll handlers
  const disableScroll = useCallback(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
  }, []);

  const enableScroll = useCallback(() => {
    document.body.style.overflow = '';
    document.body.style.touchAction = '';

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }
  }, []);

  const enableScrollWithTimeout = useCallback(
    () => {
      scrollTimeoutRef.current = setTimeout(() => {
        enableScroll();
      }, 5000);
    },
    [enableScroll],
  );

  // Move with hardware acceleration
  const moveWithinBounds = useCallback(
    (clientX, clientY) => {
      if (!wrapperRef.current) return;

      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      const rect = wrapperRef.current.getBoundingClientRect();
      const elementWidth = rect.width;
      const elementHeight = rect.height;

      // Calculate new position based on the distance moved from the starting drag position
      const moveX = clientX - offset.current.x;
      const moveY = clientY - offset.current.y;

      // Keep within bounds
      const boundedX = Math.max(0, Math.min(moveX, windowWidth - elementWidth));
      const boundedY = Math.max(
        topMargin,
        Math.min(moveY, windowHeight - elementHeight - bottomMargin),
      );

      updatePosition(boundedX, boundedY);

      // Check if this is a click or a drag
      const moveDistance = Math.sqrt(
        (clientX - mouseDownPos.current.x) ** 2 +
          (clientY - mouseDownPos.current.y) ** 2,
      );
      // If moved more than 5px, it's a drag, not a click
      if (moveDistance > 5) {
        isClick.current = false;
      }

      // If we've moved more than a tiny amount, consider it "dragged"
      // (used to determine whether to apply animation class)
      if (
        Math.abs(boundedX - startPos.current.x) > 5 ||
        Math.abs(boundedY - startPos.current.y) > 5
      ) {
        setHasBeenDragged(true);
      }
    },
    [bottomMargin, topMargin, updatePosition],
  );

  // Improved snap with smooth animation
  const snapToCorner = useCallback(
    () => {
      if (!wrapperRef.current) return;

      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      const rect = wrapperRef.current.getBoundingClientRect();
      const elementWidth = rect.width;
      const elementHeight = rect.height;

      // Calculate distances to each corner
      const leftDist = rect.left;
      const rightDist = windowWidth - rect.right;
      const topDist = rect.top - topMargin;
      const bottomDist = windowHeight - rect.bottom - bottomMargin;

      // Determine nearest corner
      const snapToLeft = leftDist < rightDist;
      const snapToTop = topDist < bottomDist;

      const snapX = snapToLeft ? 10 : windowWidth - elementWidth - 10;
      const snapY = snapToTop
        ? topMargin + 10
        : windowHeight - elementHeight - bottomMargin - 10;

      // Apply CSS transition for smooth animation
      const animateStyle = {
        transition: 'transform 0.8s cubic-bezier(0.25, 0.1, 0.25, 1)',
      };
      updatePosition(snapX, snapY, animateStyle);

      // Remove transition after animation completes
      setTimeout(() => {
        if (wrapperRef.current) {
          wrapperRef.current.style.transition = '';
        }
      }, 300);
    },
    [bottomMargin, topMargin, updatePosition],
  );

  // Event handlers
  const handleMove = useCallback(
    e => {
      if (!isDragging.current) return;

      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;

      // Check if this is actually a drag or just a small movement
      const moveDistance = Math.sqrt(
        (clientX - mouseDownPos.current.x) ** 2 +
          (clientY - mouseDownPos.current.y) ** 2,
      );

      // Only treat as a drag if movement is significant
      if (moveDistance > 5) {
        e.preventDefault();
        e.stopPropagation();
        isClick.current = false;
        moveWithinBounds(clientX, clientY);
      }
    },
    [moveWithinBounds],
  );

  const handleEnd = useCallback(
    e => {
      if (!isDragging.current) return;

      // Only stop propagation if it was truly a drag, not a click
      if (!isClick.current) {
        e.stopPropagation();
        snapToCorner();
      }

      isDragging.current = false;
      listeners.current.removeListeners();
      enableScroll();
    },
    [enableScroll, snapToCorner],
  );

  const handleStart = useCallback(
    e => {
      if (!isDraggable || !wrapperRef.current) return;
      if (e?.target?.dataset?.noDrag) return;

      // Reset click detection
      isClick.current = true;
      mouseDownPos.current = {
        x: e.touches ? e.touches[0].clientX : e.clientX,
        y: e.touches ? e.touches[0].clientY : e.clientY,
      };
      isDragging.current = true;

      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;

      const rect = wrapperRef.current.getBoundingClientRect();

      // Store the current position for later comparison
      startPos.current = {
        x: rect.left,
        y: rect.top,
      };

      offset.current = {
        x: clientX - rect.left,
        y: clientY - rect.top,
      };

      listeners.current.addListeners();
      disableScroll();
      enableScrollWithTimeout();

      // Don't preventDefault here - we'll let the handleMove determine if this is a drag
      // e.preventDefault();
    },
    [isDraggable, disableScroll, enableScrollWithTimeout],
  );

  // const onClick = useCallback(
  //   e => {
  //     if (e?.target?.dataset?.noDrag) return;
  //     // We need to let real clicks pass through while still allowing drag operations
  //     // Don't prevent default at all in the click handler
  //   },
  //   [isDraggable],
  // );

  // Cleanup
  useEffect(
    () => () => {
      enableScroll();
      if (listeners.current.removeListeners) {
        listeners.current.removeListeners();
      }

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    },
    [enableScroll],
  );
  // useEffect(
  //   () => {
  //     if (wrapperRef.current && s) {
  //       const cssClass = isDraggable
  //         ? `${s.draggable}${
  //             !hasBeenDragged && animation ? ` ${animation}` : ''
  //           }`
  //         : s.nodrag;
  //       wrapperRef.current.className = cssClass;
  //     }
  //   },
  //   [hasBeenDragged, animation, isDraggable, wrapperRef, s],
  // );
  useEffect(
    () => {
      // Add event listeners to the element
      if (wrapperRef.current) {
        wrapperRef.current.addEventListener('mousedown', handleStart);
        wrapperRef.current.addEventListener('touchstart', handleStart);
        // wrapperRef.current.addEventListener('click', onClick);
      }

      return () => {
        if (wrapperRef.current) {
          wrapperRef.current.removeEventListener('mousedown', handleStart);
          wrapperRef.current.removeEventListener('touchstart', handleStart);
          // wrapperRef.current.removeEventListener('click', onClick);
        }
      };
    },
    [handleStart, wrapperRef],
  );

  // Setup listeners functions in a single useEffect to avoid circular dependencies
  useEffect(
    () => {
      // Setup listeners once our callbacks are defined
      listeners.current.addListeners = () => {
        document.addEventListener('mousemove', handleMove, { passive: false });
        document.addEventListener('mouseup', handleEnd);
        document.addEventListener('touchmove', handleMove, { passive: false });
        document.addEventListener('touchend', handleEnd);
      };

      listeners.current.removeListeners = () => {
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleEnd);
        document.removeEventListener('touchmove', handleMove);
        document.removeEventListener('touchend', handleEnd);
      };
    },
    [handleMove, handleEnd],
  );

  return <div />;
};

// Add prop types validation
DraggableWrapper.propTypes = {
  wrapperRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element),
  }).isRequired,
  isDraggable: PropTypes.bool,
  updatePlayerState: PropTypes.func.isRequired,
  bottomMargin: PropTypes.number,
  topMargin: PropTypes.number,
  animation: PropTypes.string,
};
// Add default props
DraggableWrapper.defaultProps = {
  isDraggable: true,
  bottomMargin: 130, // Default bottom margin
  topMargin: 40, // Default top margin
  animation: undefined, // Default animation
};

export default DraggableWrapper;
